import { useState } from 'react';
import { useVerifyMtoMappingMutation } from '@shared/api';
import type { NsiCandidate, NsiMapping } from '@entities/mto';
import {
  apiErrorMessage,
  fmtConfidence,
  mappingStatusLabel,
  mappingStatusTone,
  matchTypeLabel,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { MAPPINGS_PAGE, mappedNomenclatureName } from '../helpers';
import {
  CandidateCell,
  CandidateHint,
  CandidateItem,
  CandidateList,
  CandidateMeta,
  CandidateName,
  CandidateSub,
  ExpandButton,
  MappedName,
  MoreRow,
  Muted,
  RowActions,
  Table,
  TableScroll,
  TrReview,
} from '../styles';

/** Одна строка сопоставления с НСИ + раскрывающийся список кандидатов (для
 *  status `pending`/`auto`): выбрать-и-подтвердить кандидата либо отклонить. */
export function MappingRow({
  mapping,
  runId,
  canWrite,
  onError,
}: {
  mapping: NsiMapping;
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [verify, { isLoading }] = useVerifyMtoMappingMutation();

  // Уточнить можно сопоставление, которое ещё не финализировано снабженцем:
  // авто-проставленное агентом или вынесенное на проверку.
  const refinable = mapping.status === 'pending' || mapping.status === 'auto';
  const hasCandidates = mapping.candidates.length > 0;
  const mappedName = mappedNomenclatureName(mapping);
  // Строки на проверку подсвечиваем мягким фоном (не только тонкой границей —
  // низкий контраст плохо читается при нарушениях цветовосприятия).
  const review = mapping.status === 'pending';

  async function handleVerify(candidate: NsiCandidate) {
    onError('');
    try {
      await verify({
        id: mapping.id,
        runId,
        nomenclature_id: candidate.nomenclature_id,
        decision: 'verified',
      }).unwrap();
      setOpen(false);
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось подтвердить сопоставление.');
      // eslint-disable-next-line no-console
      console.error('verifyMtoMapping (verified) failed', err);
    }
  }

  async function handleReject() {
    onError('');
    try {
      await verify({ id: mapping.id, runId, nomenclature_id: null, decision: 'rejected' }).unwrap();
      setOpen(false);
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось отклонить сопоставление.');
      // eslint-disable-next-line no-console
      console.error('verifyMtoMapping (rejected) failed', err);
    }
  }

  return (
    <>
      <TrReview $review={review}>
        <td>
          {mapping.source_text}
          {refinable && hasCandidates && (
            <>
              <br />
              <ExpandButton
                type="button"
                $open={open}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
              >
                {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                Варианты ({mapping.candidates.length})
              </ExpandButton>
            </>
          )}
        </td>
        <td>
          <Badge $tone="neutral">{matchTypeLabel(mapping.match_type)}</Badge>
        </td>
        <td className="num">{fmtConfidence(mapping.confidence)}</td>
        <td>{mappedName ? <MappedName>{mappedName}</MappedName> : '—'}</td>
        <td>
          <Badge $tone={mappingStatusTone(mapping.status)}>
            {mappingStatusLabel(mapping.status)}
          </Badge>
        </td>
        <td>
          {canWrite && refinable ? (
            <RowActions>
              {hasCandidates ? (
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={isLoading}
                  onClick={() => setOpen((v) => !v)}
                >
                  {open ? 'Скрыть' : 'Выбрать'}
                </Button>
              ) : (
                <Muted as="span">нет вариантов</Muted>
              )}
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={isLoading}
                onClick={handleReject}
              >
                Отклонить
              </Button>
            </RowActions>
          ) : (
            <Muted as="span">{canWrite ? 'решение принято' : '—'}</Muted>
          )}
        </td>
      </TrReview>
      {open && refinable && hasCandidates && (
        <tr>
          <CandidateCell colSpan={6}>
            <CandidateList>
              <CandidateHint>
                Выберите материал из справочника — он будет привязан к позиции потребности.
              </CandidateHint>
              {mapping.candidates.map((c) => (
                <CandidateItem key={c.nomenclature_id}>
                  <CandidateMeta>
                    <CandidateName>{c.name}</CandidateName>
                    <CandidateSub>
                      <span>код: {c.code ?? '—'}</span>
                      <span>ед.: {c.unit ?? '—'}</span>
                      <span>совпадение: {fmtConfidence(c.confidence)}</span>
                    </CandidateSub>
                  </CandidateMeta>
                  <Button
                    type="button"
                    $variant="primary"
                    $size="sm"
                    disabled={isLoading}
                    onClick={() => handleVerify(c)}
                  >
                    Привязать
                  </Button>
                </CandidateItem>
              ))}
            </CandidateList>
          </CandidateCell>
        </tr>
      )}
    </>
  );
}

/** Блок «Сопоставление с НСИ» — таблица нормализации позиций потребности к
 *  справочнику номенклатуры с HITL (подтвердить кандидата / отклонить). */
export function MappingsSection({
  mappings,
  runId,
  canWrite,
  onError,
}: {
  mappings: NsiMapping[];
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [shown, setShown] = useState(MAPPINGS_PAGE);
  if (mappings.length === 0) {
    return <Muted>Сопоставления появятся после завершения обработки.</Muted>;
  }
  // На проверку — наверх: сначала pending, затем auto (уточняемые), потом
  // финализированные (verified/rejected).
  const sorted = [...mappings].sort((a, b) => {
    const score = (m: NsiMapping) => (m.status === 'pending' ? 2 : m.status === 'auto' ? 1 : 0);
    return score(b) - score(a);
  });
  return (
    <>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <th scope="col">Позиция</th>
              <th scope="col">Тип совпадения</th>
              <th scope="col" className="num">
                Уверенность
              </th>
              <th scope="col">Материал из справочника</th>
              <th scope="col">Статус</th>
              <th scope="col" aria-label="Действие" />
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, shown).map((m) => (
              <MappingRow
                key={m.id}
                mapping={m}
                runId={runId}
                canWrite={canWrite}
                onError={onError}
              />
            ))}
          </tbody>
        </Table>
      </TableScroll>
      {sorted.length > shown && (
        <MoreRow>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => setShown((n) => n + MAPPINGS_PAGE)}
          >
            Показать ещё {Math.min(MAPPINGS_PAGE, sorted.length - shown)}
          </Button>
        </MoreRow>
      )}
    </>
  );
}
